// client/src/pages/seo/SpeisekarteOnlineVerwaltenDe.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import SeoRelatedLinks from "../../components/seo/SeoRelatedLinks.jsx";
import SeoHeader from "../../components/seo/SeoHeader.jsx";
import SeoContactSection from "../../components/seo/SeoContactSection.jsx";
import SeoWorkSteps from "../../components/seo/SeoWorkSteps.jsx";

const sections = [
  {
    id: "check",
    label: "Braucht Ihr Restaurant Online-Menüverwaltung?",
  },
  {
    id: "definition",
    label: "Was bedeutet es, eine Speisekarte online zu verwalten?",
  },
  {
    id: "costs",
    label: "Warum schnelle Preisänderungen für Restaurants wichtig sind",
  },
  {
    id: "availability",
    label: "Gerichte ausblenden, wenn sie nicht verfügbar sind",
  },
  {
    id: "daily-menu",
    label: "Tagesmenüs und saisonale Angebote schneller veröffentlichen",
  },
  {
    id: "photos",
    label: "Fotos und Beschreibungen direkt verbessern",
  },
  {
    id: "pdf",
    label: "Warum PDF-Speisekarten bei häufigen Änderungen schwierig sind",
  },
  {
    id: "workflow",
    label: "Wie ein guter Ablauf für Menüverwaltung aussieht",
  },
  {
    id: "mistakes",
    label: "Häufige Fehler bei der Online-Menüverwaltung",
  },
  {
    id: "bestplace",
    label: "Wie Best Place bei der Online-Menüverwaltung hilft",
  },
  {
    id: "faq",
    label: "FAQ zur Online-Menüverwaltung",
  },
];

const checklist = [
  "Sie ändern regelmäßig Preise oder Tagesangebote.",
  "Einige Gerichte sind zeitweise ausverkauft.",
  "Sie möchten Produkte schnell ausblenden und später wieder anzeigen.",
  "Sie möchten Fotos, Beschreibungen oder Kategorien selbst bearbeiten.",
  "Sie möchten nicht für jede Änderung eine neue PDF-Datei erstellen.",
  "Sie möchten weniger von Druckereien, Designern oder Entwicklern abhängig sein.",
  "Gäste prüfen Ihre Speisekarte bereits vor dem Besuch online.",
  "Sie möchten saisonale Angebote schneller veröffentlichen.",
  "Ihr Team soll mit aktuellen Menüinformationen arbeiten.",
];

const CALLBACK_API_ORIGIN =
  typeof window !== "undefined" &&
  (window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1")
    ? "http://localhost:3030"
    : "";

function getResult(score) {
  if (score <= 3) {
    return {
      title: "Online-Menüverwaltung ist vielleicht noch nicht dringend",
      text:
        "Wenn sich Ihre Speisekarte nur selten ändert, kann eine einfache digitale Version vorerst ausreichen. Trotzdem können einfachere Aktualisierungen mit der Zeit sehr nützlich werden.",
      tone: "low",
    };
  }

  if (score <= 6) {
    return {
      title: "Online-Menüverwaltung ist sinnvoll",
      text:
        "Ihr Restaurant kann davon profitieren, Preise, Verfügbarkeit, Fotos und Tagesangebote schneller zu ändern, ohne Dateien neu zu erstellen oder erneut drucken zu müssen.",
      tone: "medium",
    };
  }

  return {
    title: "Online-Menüverwaltung kann den Alltag stark erleichtern",
    text:
      "Mehrere Punkte sprechen dafür, dass Ihr Restaurant eine bearbeitbare Online-Speisekarte braucht: aktuelle Preise, ausgeblendete ausverkaufte Gerichte, Tagesangebote, Fotos und weniger Aufwand für Änderungen.",
    tone: "high",
  };
}

function VisualIdea({ children }) {
  return (
    <div className="seoVisualIdea" aria-label="Idee für Illustration">
      <div className="seoVisualIdea__icon" aria-hidden="true">
        ✦
      </div>
      <div>
        <strong>Idee für Illustration:</strong>
        <p>{children}</p>
      </div>
    </div>
  );
}

export default function SpeisekarteOnlineVerwaltenDe() {
  const [checked, setChecked] = useState([]);

  const score = checked.length;
  const result = useMemo(() => getResult(score), [score]);

  useEffect(() => {
    document.documentElement.setAttribute("lang", "de");
    document.title =
      "Speisekarte online verwalten: Preise, Angebote und Verfügbarkeit | Best Place";
  }, []);

  const toggle = (item) => {
    setChecked((prev) =>
      prev.includes(item) ? prev.filter((x) => x !== item) : [...prev, item]
    );
  };

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  return (
    <main className="qrArticle">
      <SeoHeader
        lang="de"
        langLinks={{
          en: "/en/manage-menu-online-for-restaurants",
          de: "/de/speisekarte-online-verwalten",
          no: "/no/administrere-meny-online-for-restauranter",
        }}
      />

      <section className="qrArticle__hero">
        <div className="qrArticle__heroInner">
          <h1 className="qrArticle__heroTitle">
            <span>
              Restaurant-Speisekarte online verwalten: Preise, Tagesangebote und
              Verfügbarkeit schneller aktualisieren
            </span>
          </h1>

          <div className="qrArticle__topsection">
            <div className="qrArticle__topsection--img-wrapper">
              <div className="qrArticle__topsection--img"></div>
            </div>

            <div className="qrArticle__topsection--text-wrapper">
              <p className="seoHero__lead">
                Eine bearbeitbare Online-Speisekarte hilft Restaurants,
                schneller zu reagieren: Preise aktualisieren, ausverkaufte
                Gerichte ausblenden, Tagesangebote veröffentlichen und Fotos
                oder Beschreibungen verbessern, ohne jedes Mal eine PDF neu zu
                erstellen.
              </p>

              <p className="seoHero__text">
                Der Vorteil liegt nicht nur im Design. Der echte Nutzen zeigt
                sich im Alltag: Wenn sich etwas ändert, sollte ein Restaurant
                nicht jedes Mal eine neue Datei, einen neuen Druck oder einen
                Entwickler brauchen.
              </p>
            </div>
          </div>
        </div>
      </section>

      <SeoContactSection lang="de" apiOrigin={CALLBACK_API_ORIGIN} />

      <section className="articls">
        <h2>Inhalt des Artikels</h2>

        <nav className="qrArticle__nav" aria-label="Artikelnavigation">
          <ol className="qrArticle__navList">
            {sections.map((item, index) => (
              <li key={item.id} className="qrArticle__navItem">
                <button
                  type="button"
                  className="qrArticle__navButton"
                  onClick={() => scrollToSection(item.id)}
                >
                  <span className="qrArticle__navNumber">
                    {String(index + 1).padStart(2, "0")}
                  </span>

                  <span className="qrArticle__navLabel">{item.label}</span>
                </button>
              </li>
            ))}
          </ol>
        </nav>
      </section>

      <article>
        <section id="check" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Braucht Ihr Restaurant Online-Menüverwaltung?</h2>

            <p>
              Wählen Sie die Aussagen aus, die zu Ihrem Restaurant passen. Das
              Ergebnis ist bewusst ehrlich: Nicht jedes Restaurant braucht vom
              ersten Tag an eine erweiterte Menüverwaltung. Viele Restaurants
              profitieren aber viel früher von schnellen Aktualisierungen, als
              sie zunächst denken.
            </p>

            <div className="qrArticle__checkGrid">
              {checklist.map((item) => (
                <label key={item} className="qrArticle__checkItem">
                  <input
                    type="checkbox"
                    checked={checked.includes(item)}
                    onChange={() => toggle(item)}
                  />
                  <span className="text">{item}</span>
                </label>
              ))}
            </div>

            <div className={`qrArticle__result qrArticle__result--${result.tone}`}>
              <div className="qrArticle__score">
                {score} / {checklist.length}
              </div>

              <div>
                <strong>{result.title}</strong>
                <p>{result.text}</p>
              </div>
            </div>
          </div>
        </section>

        <section id="definition" className="acquaintance">
          <h2>Was bedeutet es, eine Speisekarte online zu verwalten?</h2>

          <p>
            Eine Speisekarte online zu verwalten bedeutet, dass ein Restaurant
            Inhalte über eine digitale Oberfläche bearbeiten kann: Kategorien,
            Gerichte, Preise, Beschreibungen, Fotos und Verfügbarkeit.
          </p>

          <p>
            Statt für jede kleine Änderung eine neue Datei zu erstellen oder
            jemanden darum zu bitten, kann der Inhaber oder Administrator die
            Speisekarte direkt aktualisieren.
          </p>

          <ul className="qrArticle__block-li">
            <li className="text__seo-li">Preise ändern.</li>
            <li className="text__seo-li">Gerichte hinzufügen.</li>
            <li className="text__seo-li">Produkte ausblenden.</li>
            <li className="text__seo-li">Fotos aktualisieren.</li>
            <li className="text__seo-li">Beschreibungen verbessern.</li>
            <li className="text__seo-li">Kategorien neu anordnen.</li>
            <li className="text__seo-li">Tagesangebote veröffentlichen.</li>
            <li className="text__seo-li">Saisonale Gerichte verwalten.</li>
          </ul>

          <h3>Die Speisekarte sollte im Alltag bearbeitbar sein</h3>

          <p>
            Eine digitale Speisekarte ist am nützlichsten, wenn sie von den
            Personen geändert werden kann, die tatsächlich mit dem Menü arbeiten:
            vom Inhaber, Manager oder verantwortlichen Teammitglied.
          </p>
        </section>

        <section id="costs" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>
              Warum schnelle Preisänderungen für Restaurants wichtig sind
            </h2>

            <p>
              Restaurants arbeiten mit wechselnden Einkaufspreisen,
              Personalkosten, Energiekosten und saisonalen Produkten. Wenn sich
              Kosten ändern, müssen manchmal Menüpreise, Angebotsstruktur oder
              Portionen angepasst werden.
            </p>

            <p>
              Eine bearbeitbare Online-Speisekarte beseitigt den Kostendruck
              nicht. Sie hilft aber, sichtbare Informationen aktuell zu halten,
              ohne jedes Mal einen vollständigen Druck- oder Dateiänderungsprozess
              zu starten.
            </p>

            <div className="seoComparison">
              <div className="seoComparison__col">
                <h3>Langsamer Änderungsprozess</h3>

                <ul className="qrArticle__block-li">
                  <li className="text__seo-li">
                    Die Preisänderung wird notiert.
                  </li>
                  <li className="text__seo-li">
                    PDF oder Druckdatei muss bearbeitet werden.
                  </li>
                  <li className="text__seo-li">
                    Jemand muss die Datei erneut hochladen.
                  </li>
                  <li className="text__seo-li">
                    Alte Versionen können weiterhin online bleiben.
                  </li>
                </ul>
              </div>

              <div className="seoComparison__col seoComparison__col--highlight">
                <h3>Besserer Ablauf</h3>

                <ul className="qrArticle__block-li">
                  <li className="text__seo-li">
                    Menüverwaltung öffnen.
                  </li>
                  <li className="text__seo-li">Preis ändern.</li>
                  <li className="text__seo-li">Änderung speichern.</li>
                  <li className="text__seo-li">
                    Die QR-Speisekarte zeigt die aktuelle Version.
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        <section id="availability" className="acquaintance">
          <h2>Gerichte ausblenden, wenn sie nicht verfügbar sind</h2>

          <p>
            Im Restaurantbetrieb ist nicht jeder Artikel immer verfügbar.
            Zutaten können fehlen, ein Gericht kann ausverkauft sein oder ein
            Angebot gilt nur zu bestimmten Zeiten.
          </p>

          <div className="seoComparison">
            <div className="seoComparison__col">
              <h3>Statische Speisekarte</h3>

              <ul className="qrArticle__block-li">
                <li className="text__seo-li">
                  Ausverkaufte Gerichte bleiben sichtbar.
                </li>
                <li className="text__seo-li">
                  Gäste bestellen etwas, das nicht verfügbar ist.
                </li>
                <li className="text__seo-li">
                  Das Personal muss erklären und enttäuschen.
                </li>
                <li className="text__seo-li">Änderungen dauern lange.</li>
              </ul>
            </div>

            <div className="seoComparison__col seoComparison__col--highlight">
              <h3>Bearbeitbare Online-Speisekarte</h3>

              <ul className="qrArticle__block-li">
                <li className="text__seo-li">
                  Gerichte können vorübergehend ausgeblendet werden.
                </li>
                <li className="text__seo-li">
                  Die Verfügbarkeit bleibt aktueller.
                </li>
                <li className="text__seo-li">Weniger Missverständnisse.</li>
                <li className="text__seo-li">
                  Artikel können später wieder aktiviert werden.
                </li>
              </ul>
            </div>
          </div>

          <p>
            Ziel ist nicht, den Service zu ersetzen. Ziel ist, vermeidbare
            Enttäuschungen zu reduzieren, bevor sie entstehen.
          </p>
        </section>

        <section id="daily-menu" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>
              Tagesmenüs und saisonale Angebote schneller veröffentlichen
            </h2>

            <p>
              Tagesgerichte, Mittagsangebote, saisonale Speisen oder kurzfristige
              Aktionen wirken am besten, wenn Gäste sie im richtigen Moment
              sehen.
            </p>

            <ol className="seoSteps">
              <li className="text__seo-li">Tagesgericht erstellen.</li>
              <li className="text__seo-li">Preis hinzufügen.</li>
              <li className="text__seo-li">Foto hinzufügen.</li>
              <li className="text__seo-li">Online sichtbar machen.</li>
              <li className="text__seo-li">Später ausblenden.</li>
            </ol>

            <p>
              Das kann besonders für Cafés, Bistros, Pizzerien und Restaurants
              mit wechselnden Angeboten nützlich sein. Die Speisekarte wird nicht
              nur digital angezeigt — sie wird aktiv verwaltet.
            </p>

            <div className="seoCompareSmall">
              <div>
                <strong>Verwandte Seite</strong>
                <p>
                  <Link to="/de/tageskarte-digital-erstellen">
                    Tageskarte digital erstellen
                  </Link>
                </p>
              </div>

              <div>
                <strong>Verwandte Seite</strong>
                <p>
                  <Link to="/de/speisekarte-online-aendern">
                    Speisekarte online ändern
                  </Link>
                </p>
              </div>
            </div>
          </div>
        </section>

        <section id="photos" className="acquaintance">
          <h2>Fotos und Beschreibungen direkt verbessern</h2>

          <p>
            Gute Fotos und klare Beschreibungen helfen Gästen, ein Gericht
            schneller zu verstehen. Das ist besonders wichtig bei neuen
            Gerichten, saisonalen Angeboten, Sets oder Speisen, die eine kurze
            Erklärung brauchen.
          </p>

          <ul className="qrArticle__block-li">
            <li className="text__seo-li">Foto ersetzen.</li>
            <li className="text__seo-li">Beschreibung kürzen.</li>
            <li className="text__seo-li">Zutaten erklären.</li>
            <li className="text__seo-li">Schärfegrad beschreiben.</li>
            <li className="text__seo-li">Beilagen zeigen.</li>
            <li className="text__seo-li">Empfehlungen hervorheben.</li>
          </ul>

          <p>
            Eine digitale Speisekarte sollte nach dem ersten Upload nicht
            „fertig“ sein. Sie sollte sich verbessern lassen, wenn das Restaurant
            lernt, was Gäste brauchen.
          </p>

          <VisualIdea>
            Eine einfache Admin-Oberfläche, in der der Inhaber ein Gerichtsfoto
            ersetzt, die Beschreibung kürzt und die aktualisierte Speisekarte auf
            einem Smartphone prüft.
          </VisualIdea>
        </section>

        <section id="pdf" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>
              Warum PDF-Speisekarten bei häufigen Änderungen schwierig sind
            </h2>

            <p>
              Eine PDF kann für den Anfang funktionieren. Wenn sich Preise,
              Tagesangebote oder Verfügbarkeit regelmäßig ändern, wird eine PDF
              jedoch unpraktisch.
            </p>

            <div className="seoComparison">
              <div className="seoComparison__col">
                <h3>PDF-Speisekarte</h3>

                <ul className="qrArticle__block-li">
                  <li className="text__seo-li">
                    Jede Änderung erzeugt eine neue Datei.
                  </li>
                  <li className="text__seo-li">
                    Alte Versionen können weiter im Umlauf bleiben.
                  </li>
                  <li className="text__seo-li">
                    Auf Smartphones oft unbequem.
                  </li>
                  <li className="text__seo-li">
                    Keine einfache Sichtbarkeitslogik für ausverkaufte Artikel.
                  </li>
                </ul>
              </div>

              <div className="seoComparison__col seoComparison__col--highlight">
                <h3>Online-Speisekarte</h3>

                <ul className="qrArticle__block-li">
                  <li className="text__seo-li">
                    Änderungen passieren in der Verwaltung.
                  </li>
                  <li className="text__seo-li">
                    Die aktuelle Version ist per QR-Code erreichbar.
                  </li>
                  <li className="text__seo-li">Bessere mobile Erfahrung.</li>
                  <li className="text__seo-li">
                    Artikel können sichtbar oder ausgeblendet sein.
                  </li>
                </ul>
              </div>
            </div>

            <p>
              Wenn Gäste die Speisekarte online bereits vor dem Besuch sehen,
              sollte die sichtbare Speisekarte so aktuell wie möglich sein.
            </p>
          </div>
        </section>

        <section id="workflow" className="acquaintance">
          <h2>Wie ein guter Ablauf für Menüverwaltung aussieht</h2>

          <p>
            Gute Menüverwaltung sollte so einfach sein, dass sie im
            Restaurantalltag tatsächlich genutzt wird.
          </p>

          <ol className="seoSteps">
            <li className="text__seo-li">
              Der Administrator öffnet den Bereich für Menüverwaltung.
            </li>
            <li className="text__seo-li">
              Kategorie oder Produkt wird ausgewählt.
            </li>
            <li className="text__seo-li">
              Preis, Foto oder Beschreibung wird geändert.
            </li>
            <li className="text__seo-li">
              Das Produkt wird sichtbar gemacht, ausgeblendet oder aktualisiert.
            </li>
            <li className="text__seo-li">Änderung wird gespeichert.</li>
            <li className="text__seo-li">
              Gäste sehen die aktualisierte Version über QR-Code oder Link.
            </li>
          </ol>

          <p>
            Der Ablauf muss nicht kompliziert sein. Entscheidend ist, dass
            Änderungen nicht verzögert werden, nur weil der Prozess zu schwer
            ist.
          </p>
        </section>

        <section id="mistakes" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Häufige Fehler bei der Online-Menüverwaltung</h2>

            <p>
              Online-Menüverwaltung hilft nur dann, wenn der Ablauf für das
              Restaurantteam klar ist. Wenn die Bearbeitung verwirrend ist, wird
              die Speisekarte trotzdem veraltet.
            </p>

            <div className="seoComparison">
              <div className="seoComparison__col">
                <h3>Häufige Fehler</h3>

                <ul className="qrArticle__block-li">
                  <li className="text__seo-li">
                    Zu viele Pflichtfelder für kleine Änderungen.
                  </li>
                  <li className="text__seo-li">
                    Betreiber verstehen nicht klar, ob ein Artikel öffentlich
                    sichtbar oder ausgeblendet ist.
                  </li>
                  <li className="text__seo-li">
                    Alte Preise bleiben online.
                  </li>
                  <li className="text__seo-li">
                    Schlechte oder veraltete Fotos bleiben sichtbar.
                  </li>
                  <li className="text__seo-li">
                    Tagesangebote bleiben zu lange sichtbar.
                  </li>
                  <li className="text__seo-li">
                    Die öffentliche Smartphone-Ansicht wird nach Änderungen
                    nicht geprüft.
                  </li>
                </ul>
              </div>

              <div className="seoComparison__col seoComparison__col--highlight">
                <h3>Besser</h3>

                <ul className="qrArticle__block-li">
                  <li className="text__seo-li">
                    Bearbeitung einfach und schnell halten.
                  </li>
                  <li className="text__seo-li">
                    Sichtbarkeit klar darstellen.
                  </li>
                  <li className="text__seo-li">
                    Preise aktualisieren, wenn sie sich ändern.
                  </li>
                  <li className="text__seo-li">
                    Schwache Fotos ersetzen.
                  </li>
                  <li className="text__seo-li">
                    Alte Tagesangebote entfernen oder ausblenden.
                  </li>
                  <li className="text__seo-li">
                    Speisekarte so prüfen, wie Gäste sie sehen.
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        <div id="bestplace" className="qrArticle__anchorTarget">
          <SeoWorkSteps lang="de" />
        </div>

        <section id="faq" className="acquaintance">
          <h2>Häufige Fragen zur Online-Menüverwaltung</h2>

          <div className="seoFaq">
            <details>
              <summary>Kann ich Preise selbst ändern?</summary>
              <p>
                Ja. Das ist einer der wichtigsten Vorteile einer bearbeitbaren
                Online-Speisekarte.
              </p>
            </details>

            <details>
              <summary>Kann ich ausverkaufte Gerichte ausblenden?</summary>
              <p>
                Ja. Eine gute digitale Menüverwaltung sollte ermöglichen,
                Produkte vorübergehend auszublenden und später wieder zu
                aktivieren.
              </p>
            </details>

            <details>
              <summary>Reicht eine PDF-Speisekarte aus?</summary>
              <p>
                Für einen einfachen Start kann eine PDF ausreichen. Wenn
                Änderungen häufig vorkommen, ist eine bearbeitbare
                Online-Speisekarte meistens praktischer.
              </p>
            </details>

            <details>
              <summary>Brauche ich einen Entwickler für Änderungen?</summary>
              <p>
                Ziel einer guten Menüverwaltung ist, dass Inhaber oder
                Administratoren wichtige Änderungen selbst vornehmen können.
              </p>
            </details>

            <details>
              <summary>Kann ich Tagesangebote online ändern?</summary>
              <p>
                Ja. Tagesgerichte, saisonale Angebote oder Aktionen sind typische
                Anwendungsfälle für Online-Menüverwaltung.
              </p>
            </details>
          </div>
        </section>
        <SeoRelatedLinks
          lang="de"
          group="menu"
          exclude="/de/speisekarte-online-verwalten"
        />
      </article>
    </main>
  );
}